// MUI
import { Box, Typography } from "@mui/material";
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from "@mui/lab";

// Third Party
import clsx from "clsx";
import moment from "moment";

// Components
import { IReconciliationNoteHistory } from "@/DataPages/types/inventory/models/reconciliation";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

interface INotesVersionHistoryTimelineProps {
	orderedDescNotes: IReconciliationNoteHistory[];
	onSelected: (selected: number) => void;
	historySelected: number;
	editable?: boolean;
}

const DEFAULT_CLASS_NAME = "!font-montserrat !text-xs !font-medium";
const SELECTED_TEXT_CLASS_NAME = "!text-primary";
const DEFAULT_TEXT_CLASS_NAME = "!text-default";

const NotesVersionHistoryTimeline = (props: INotesVersionHistoryTimelineProps) => {
	const { orderedDescNotes, onSelected, historySelected, editable } = props;

	return (
		<Box sx={{ height: "210px", width: "100%", marginY: "12px", paddingY: "4px", overflowY: "auto" }}>
			<Timeline
				sx={{
					"& .MuiTimelineItem-root": {
						height: "56px",
						minHeight: "56px",
					},
					"& .MuiTimelineItem-missingOppositeContent:before": {
						display: "none",
					},
					"& .MuiTimelineConnector-root": {
						flexGrow: 1,
						overflow: "hidden",
					},
					height: "234px",
					paddingLeft: "0px",
					paddingTop: "0px",
				}}
			>
				{editable && (
					<TimelineItem
						onClick={() => onSelected(-1)}
						className={clsx("cursor-pointer", historySelected === -1 ? "mt-2" : "ml-0.5")}
					>
						<TimelineSeparator>
							{historySelected === -1 ? (
								<TimelineDot variant="outlined" className="!-mt-1 !border-primary w-6 h-6" sx={{ paddingY: 0 }}>
									<Box className="flex items-center justify-center h-full w-full">
										<TimelineDot className="!mt-[4px] !bg-primary w-3 h-3" sx={{ paddingY: 0 }} />
									</Box>
								</TimelineDot>
							) : (
								<TimelineDot className="!bg-primary w-3 h-3" sx={{ margin: 0.5 }} />
							)}
							{orderedDescNotes?.length > 0 && (
								<TimelineConnector className={clsx("!bg-primary", historySelected === -1 ? "!-mt-2" : "")} />
							)}
						</TimelineSeparator>
						<TimelineContent className={"!-ml-1 !-mt-1"}>
							<Typography
								className={clsx(
									DEFAULT_CLASS_NAME,
									historySelected === -1 ? SELECTED_TEXT_CLASS_NAME : DEFAULT_TEXT_CLASS_NAME
								)}
							>
								Current
							</Typography>
						</TimelineContent>
					</TimelineItem>
				)}
				{orderedDescNotes.map((note, index) => {
					const noteKey = `note-${index}`;
					return (
						<TimelineItem
							key={noteKey}
							onClick={() => onSelected(index)}
							className={clsx("cursor-pointer", historySelected === index ? "mt-2" : "ml-0.5")}
						>
							<TimelineSeparator>
								{historySelected === index ? (
									<TimelineDot variant="outlined" className="!-mt-1 !border-primary w-6 h-6" sx={{ paddingY: 0 }}>
										<Box className="flex items-center justify-center h-full w-full">
											<TimelineDot className="!mt-[4px] !bg-primary w-3 h-3" sx={{ paddingY: 0 }} />
										</Box>
									</TimelineDot>
								) : (
									<TimelineDot className="!bg-primary w-3 h-3" sx={{ margin: 0.5 }} />
								)}
								{index !== orderedDescNotes.length - 1 && (
									<TimelineConnector className={clsx("!bg-primary", historySelected === index ? "!-mt-2" : "")} />
								)}
							</TimelineSeparator>
							<TimelineContent className={"!-ml-1 !-mt-3"}>
								<>
									<Typography
										className={clsx(
											DEFAULT_CLASS_NAME,
											historySelected === index ? SELECTED_TEXT_CLASS_NAME : DEFAULT_TEXT_CLASS_NAME
										)}
									>
										{moment(note.createdAt).format(DATE_FORMAT.fullOrdinalTime12h)}
									</Typography>
									<Typography
										className={clsx(
											DEFAULT_CLASS_NAME,
											historySelected === index ? SELECTED_TEXT_CLASS_NAME : DEFAULT_TEXT_CLASS_NAME
										)}
									>
										{note.createdByFullName ?? note.createdBy}
									</Typography>
								</>
							</TimelineContent>
						</TimelineItem>
					);
				})}
			</Timeline>
		</Box>
	);
};

export default NotesVersionHistoryTimeline;
